import React from 'react'

import { GroupBase, SelectInstance } from 'chakra-react-select'
import { FormikProps } from 'formik'
import * as yup from 'yup'

import { Alert, Text, Tooltip } from '@chakra-ui/react'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Form,
  Icon,
  Select,
  Spinner,
  Stack,
  Switch,
  Typography,
  useCheckboxGroup,
  useToast,
} from '@pnld/components-web'

import { useQueryGetPredictionVersions } from '@/api/auxiliaries/queries'
import { TStages } from '@/api/auxiliaries/types'
import { cancelAllPredictions } from '@/api/predictions/services'
import { stateOptions } from '@/data/dashboard'
import { IEducationStageDTO } from '@/DTOs/auxiliaries/auxiliaries.DTO'

import { usePredictionsController } from './controller'

interface ISelectOption extends IEducationStageDTO {
  value: string
  label: string
}

interface ISearchFormValues {
  year: string
  version: string
  all_education_stages: boolean
  pnld_stages: boolean
  child_education: Array<string>
  basic_education: Array<string>
  high_school: Array<string>
  technical_course: Array<string>
  eja: Array<string>
  location: string
  state: string
  city: string
  dependency: string
  school: string
}

const dependencyOptions = [
  { value: '1', label: 'Federal' },
  { value: '2', label: 'Estadual' },
  { value: '3', label: 'Municipal' },
  { value: '4', label: 'Privada' },
]

const locationOptions = [
  { value: '1', label: 'Urbana' },
  { value: '2', label: 'Rural' },
]

const Predictions = () => {
  const {
    isPredictionsPreviewLoading,
    educationStages,
    isEducationStagesLoading,
    availableUntilYear,
    predictionYearsOptions,
    isPredictionYearsLoading,
    setPredictionsFilters,
    isSearchEmpty,
    setIsSearchEmpty,
    checkPredictionsData,
    refetchCheckPredictions,
    isCheckingPredictions,
    regionInfo,
    setRegionInfo,
    citiesByUf,
    isCitiesLoading,
    schoolsByCity,
    isSchoolsLoading,
  } = usePredictionsController()
  const toast = useToast()
  const [selectedYear, setSelectedYear] = React.useState<string>('')
  const [allEducationStages, setAllEducationStages] = React.useState(false)
  const [pnldStages, setPnldStages] = React.useState(false)
  const [moreFilters, setMoreFilters] = React.useState(false)
  const processingPrediction = !!checkPredictionsData?.processingPrediction
  const previousStateRef = React.useRef<string>('')
  const formikRef = React.useRef<FormikProps<ISearchFormValues>>(null)
  const selectRefs = React.useRef<
    Record<
      string,
      SelectInstance<unknown, true | false, GroupBase<unknown>> | null
    >
  >({})
  const {
    value: spheresCheckboxesValues,
    getCheckboxProps: getSpheresCheckboxProps,
    setValue: setSpheresCheckboxes,
  } = useCheckboxGroup({
    defaultValue: [],
  })
  const {
    data: predictionVersions = [],
    isLoading: isPredictionVersionsLoading,
  } = useQueryGetPredictionVersions(
    { nuCensusYear: selectedYear },
    { enabled: !!selectedYear }
  )

  const predictionVersionsOptions = React.useMemo(
    () =>
      predictionVersions.map(v => ({
        label: v.label,
        value: String(v.value),
      })),
    [predictionVersions]
  )

  React.useEffect(() => {
    if (
      !isPredictionVersionsLoading &&
      predictionVersionsOptions.length === 1
    ) {
      const option = predictionVersionsOptions[0]
      formikRef.current?.setFieldValue('version', option.value)
      ;(selectRefs.current.version as any)?.setValue(option, 'select-option')
    }
  }, [predictionVersionsOptions, isPredictionVersionsLoading])

  const initialValues = {
    year: '',
    version: '',
    all_education_stages: false,
    pnld_stages: false,
    child_education: [],
    basic_education: [],
    high_school: [],
    technical_course: [],
    eja: [],
    state: '',
    city: '',
    dependency: '',
    school: '',
    location: '',
  }

  const getEducationStagesOptions = (key: TStages) => {
    const rawOptions = educationStages?.[key]
    const options = rawOptions?.map(option => ({
      value: String(option.co_education_stage),
      label: option.no_education_stage,
      ...option,
    }))

    return options as Array<{
      value: string
      label: string
    }>
  }

  const handleSearch = (
    values: ISearchFormValues,
    educationStages: string[]
  ) => {
    setIsSearchEmpty(false)

    const coEducationStageForApi = !(allEducationStages || pnldStages)
      ? educationStages.join(',')
      : ''

    const coEducationStageForDisplay = !(allEducationStages || pnldStages)
      ? educationStages.join(',')
      : ''

    setPredictionsFilters({
      nuCensusYear: String(values.year),
      nuYearPrediction: String(values.year),
      nuVersao: values.version ? Number(values.version) : undefined,
      coEducationStage: coEducationStageForApi,
      coEducationStageDisplay: coEducationStageForDisplay,
      coSphere: spheresCheckboxesValues.map(v => Number(v)),
      isAllStages: allEducationStages,
      stPnldService: pnldStages,
      coCity: values.city,
      coUf: values.state,
      tpLocation: values.location,
      coEntity: values.school,
      tpDependency: values.dependency,
    })
  }

  const handleSearchPreConditions = async (values: ISearchFormValues) => {
    if (spheresCheckboxesValues.length === 0) {
      toast({
        status: 'warning',
        title: 'Selecione ao menos uma esfera.',
        isClosable: true,
        position: 'bottom',
      })
      return
    }

    const educationStages = [
      ...values.child_education,
      ...values.basic_education,
      ...values.high_school,
      ...values.technical_course,
      ...values.eja,
    ]

    if (!allEducationStages && !pnldStages && educationStages.length === 0) {
      toast({
        status: 'warning',
        title: 'Selecione ao menos uma etapa de ensino.',
        isClosable: true,
        position: 'bottom',
      })
      return
    }

    const { data } = await refetchCheckPredictions()

    handleSearch(values, educationStages)
  }

  const handleCleanFilters = () => {
    formikRef.current?.resetForm({ values: initialValues })
    Object.values(selectRefs.current).forEach(ref => ref?.clearValue())
    setSelectedYear('')
    setRegionInfo({
      sgUf: '',
      noCity: '',
      noSchool: '',
    })
    setSpheresCheckboxes([])
    setAllEducationStages(false)
    setPnldStages(false)
    setMoreFilters(false)
  }

  React.useEffect(() => {
    const currentValues = formikRef.current?.values
    if (currentValues) {
      const currentState = currentValues.state || ''

      if (previousStateRef.current !== currentState) {
        if (previousStateRef.current !== '') {
          formikRef.current?.setFieldValue('city', '')
          selectRefs.current.city?.clearValue()
        }
        previousStateRef.current = currentState
      }

      setRegionInfo({
        sgUf: currentState,
        noCity: currentValues.city || '',
        noSchool: currentValues.school || '',
      })
    }
  }, [
    formikRef.current?.values?.state,
    formikRef.current?.values?.city,
    formikRef.current?.values?.school,
  ])

  return (
    <Stack gap={6}>
      {availableUntilYear > 0 && (
        <Flex
          p={4}
          gap={4}
          bg="brand.primary.light_2"
          borderRadius="md"
          align="center"
          data-testid="info-last-prediction"
        >
          <Icon name="info-circle" size="xs" />
          <Text fontWeight="semibold" color="brand.neutral.dark_2">
            Predição disponível até o ano de {availableUntilYear} com base no
            censo de {formikRef.current?.values?.year || availableUntilYear}.
          </Text>
        </Flex>
      )}

      {processingPrediction && (
        <ProcessingPredictionAlert refetchStatus={refetchCheckPredictions} />
      )}
      <Stack gap={4} boxShadow="md" p={4} borderRadius="base">
        <Typography
          variant="h-xs"
          color="brand.primary.dark_2"
          data-testid="predictions-title"
        >
          Predições
        </Typography>
        <Stack gap={4}>
          <Form
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={yup.object().shape({
              year: yup.string().required('Obrigatório'),
            })}
            onSubmit={handleSearchPreConditions}
          >
            <Stack gap={4} data-testid="predictions-filters">
              <Flex justify="space-between" gap={4} flexWrap="wrap">
                <Box
                  flex={1}
                  minW="200px"
                  data-testid="predictions-year-select"
                >
                  <Select
                    selectRef={ref => (selectRefs.current.year = ref)}
                    name="year"
                    options={predictionYearsOptions}
                    placeholder="Selecione o ano de censo base para predição"
                    isLoading={isPredictionYearsLoading}
                    disabled={isPredictionYearsLoading}
                    onChange={(option: { value: number } | null) => {
                      setSelectedYear(option ? String(option.value) : '')
                      formikRef.current?.setFieldValue('version', '')
                      selectRefs.current.version?.clearValue()
                    }}
                  />
                </Box>
                <Box flex={1} minW="200px">
                  <Select
                    selectRef={ref => (selectRefs.current.version = ref)}
                    name="version"
                    options={predictionVersionsOptions}
                    placeholder="Selecione a versão"
                    {...({
                      isLoading: !!selectedYear && isPredictionVersionsLoading,
                    } as { isLoading: boolean })}
                    disabled={
                      !selectedYear ||
                      isPredictionVersionsLoading ||
                      predictionVersionsOptions.length <= 1
                    }
                  />
                </Box>
                <Flex gap={2} flex={1} justify="flex-end" minW="180px">
                  <Button
                    label="Limpar filtros"
                    variant="tertiary"
                    onClick={handleCleanFilters}
                  />
                  <Button
                    type="submit"
                    label="Buscar"
                    variant="secondary"
                    iconName="search"
                    isLoading={
                      isPredictionsPreviewLoading || isCheckingPredictions
                    }
                  />
                </Flex>
              </Flex>
              <Flex gap={6}>
                <Flex align="center" gap={2}>
                  <Text
                    fontWeight="semibold"
                    fontSize="2xs"
                    data-testid="predictions-all-teaching-steps-text"
                  >
                    Todas as etapas de ensino
                  </Text>
                  <Switch
                    name="all_education_stages"
                    size="lg"
                    disabled={isPredictionsPreviewLoading || pnldStages}
                    onChange={e => setAllEducationStages(e.target.checked)}
                    data-testid="predictions-all-teaching-steps-switch"
                  />
                  <div data-testid="predictions-all-teaching-steps-typography">
                    <Typography variant="b-sm" color="brand.neutral.dark_2">
                      {allEducationStages ? 'Sim' : 'Não'}
                    </Typography>
                  </div>
                </Flex>
                <Flex align="center" gap={2}>
                  <Text
                    fontWeight="semibold"
                    fontSize="2xs"
                    data-testid="predictions-pnld-teaching-steps-text"
                  >
                    Etapas atendidas pelo PNLD
                  </Text>
                  <Tooltip
                    label="Limite sua busca para as etapas de ensino atendidas pelo PNLD."
                    hasArrow
                    placement="top"
                  >
                    <span>
                      <Icon name="circle-info" size="2xs" />
                    </span>
                  </Tooltip>
                  <Switch
                    name="pnld_stages"
                    size="lg"
                    disabled={isPredictionsPreviewLoading || allEducationStages}
                    onChange={e => setPnldStages(e.target.checked)}
                    data-testid="predictions-pnld-teaching-steps-switch"
                  />
                  <div data-testid="predictions-pnld-teaching-steps-typography">
                    <Typography variant="b-sm" color="brand.neutral.dark_2">
                      {pnldStages ? 'Sim' : 'Não'}
                    </Typography>
                  </div>
                </Flex>
              </Flex>
              <Stack>
                <Text
                  color="brand.neutral.dark_2"
                  fontWeight="bold"
                  fontSize="14px"
                >
                  Esfera
                </Text>
                <Flex gap={4} flexWrap="wrap">
                  <Checkbox
                    {...getSpheresCheckboxProps({
                      value: String(1),
                    })}
                    label="Pública"
                  />
                  <Checkbox
                    {...getSpheresCheckboxProps({
                      value: String(2),
                    })}
                    label="Privada"
                  />
                </Flex>
              </Stack>
              <Flex justify="center">
                <Button
                  label={moreFilters ? 'Menos filtros' : 'Mais filtros'}
                  iconName={moreFilters ? 'angle-up' : 'angle-down'}
                  variant="tertiary"
                  size="sm"
                  disabled={
                    isPredictionsPreviewLoading || isCheckingPredictions
                  }
                  onClick={() => {
                    setMoreFilters(!moreFilters)
                  }}
                />
              </Flex>
              {moreFilters && (
                <>
                  <Flex gap={4}>
                    <Select
                      selectRef={ref => (selectRefs.current.state = ref)}
                      label="Estado"
                      name="state"
                      options={stateOptions}
                      placeholder="Selecione uma opção"
                      {...{
                        onChange: (option: { value: string } | null) => {
                          selectRefs.current.city?.clearValue()
                          selectRefs.current.school?.clearValue()
                          formikRef.current?.setFieldValue('city', '')
                          formikRef.current?.setFieldValue('school', '')
                          const stateValue = option?.value || ''
                          setRegionInfo(prev => ({
                            ...prev,
                            sgUf: stateValue,
                            noCity: '',
                            noSchool: '',
                          }))
                        },
                      }}
                    />
                    <Select
                      selectRef={ref => (selectRefs.current.city = ref)}
                      label="Município"
                      name="city"
                      options={citiesByUf}
                      placeholder="Selecione uma opção"
                      disabled={!regionInfo.sgUf || isCitiesLoading}
                      {...{
                        onChange: (option: { value: string } | null) => {
                          selectRefs.current.school?.clearValue()
                          formikRef.current?.setFieldValue('school', '')
                          const cityValue = option?.value || ''
                          setRegionInfo(prev => ({
                            ...prev,
                            noCity: cityValue,
                            noSchool: '',
                          }))
                        },
                      }}
                    />
                  </Flex>
                  <Flex gap={4}>
                    <Select
                      selectRef={ref => (selectRefs.current.dependency = ref)}
                      label="Dependência"
                      name="dependency"
                      options={dependencyOptions}
                      placeholder="Selecione uma opção"
                    />
                    <Select
                      selectRef={ref => (selectRefs.current.school = ref)}
                      label="Escola"
                      name="school"
                      options={schoolsByCity}
                      placeholder="Selecione uma opção"
                      disabled={!regionInfo.noCity}
                    />
                    <Select
                      selectRef={ref => (selectRefs.current.location = ref)}
                      label="Localização"
                      name="location"
                      options={locationOptions}
                      placeholder="Selecione uma opção"
                    />
                  </Flex>
                  <Flex gap={4}>
                    <Select
                      label="Educação infantil"
                      name="child_education"
                      options={getEducationStagesOptions('EDUCACAO_INFANTIL')}
                      placeholder="Selecione uma opção"
                      isMulti
                      multiValueLimitDisplayed={1}
                      disabled={allEducationStages || pnldStages}
                    />
                    <Select
                      label="Ensino fundamental"
                      name="basic_education"
                      options={getEducationStagesOptions('ENSINO_FUNDAMENTAL')}
                      placeholder="Selecione uma opção"
                      isMulti
                      multiValueLimitDisplayed={1}
                      disabled={allEducationStages || pnldStages}
                    />
                    <Select
                      label="Ensino médio"
                      name="high_school"
                      options={getEducationStagesOptions('ENSINO_MEDIO')}
                      placeholder="Selecione uma opção"
                      isMulti
                      multiValueLimitDisplayed={1}
                      disabled={allEducationStages || pnldStages}
                    />
                  </Flex>
                  <Flex gap={4}>
                    <Select
                      label="Curso técnico"
                      name="technical_course"
                      options={getEducationStagesOptions('CURSO_TECNICO')}
                      placeholder="Selecione uma opção"
                      isMulti
                      multiValueLimitDisplayed={1}
                      disabled={allEducationStages || pnldStages}
                    />
                    <Select
                      label="EJA"
                      name="eja"
                      options={getEducationStagesOptions('EJA')}
                      placeholder="Selecione uma opção"
                      isMulti
                      multiValueLimitDisplayed={1}
                      disabled={allEducationStages || pnldStages}
                    />
                  </Flex>
                </>
              )}
            </Stack>
          </Form>
        </Stack>
      </Stack>
      {(isPredictionsPreviewLoading || isCheckingPredictions) && (
        <Flex justify="center" align="center" gap={4}>
          <Spinner color="brand.primary.dark_1" size="md" />
          <Text fontSize="2xs" fontWeight="medium" color="brand.neutral.dark_2">
            Carregando...
          </Text>
        </Flex>
      )}
      {isSearchEmpty && <EmptySearch />}
    </Stack>
  )
}

const ProcessingPredictionAlert = ({
  refetchStatus,
}: {
  refetchStatus: () => void
}) => {
  const toast = useToast()
  const [isCancelling, setIsCancelling] = React.useState(false)
  const isDev = process.env.REACT_APP_DEV_LOGIN === 'true'

  const handleCancel = async () => {
    setIsCancelling(true)
    try {
      const { cancelled } = await cancelAllPredictions()
      toast({
        status: 'success',
        title:
          cancelled === 0
            ? 'Nenhuma predição ativa encontrada.'
            : `${cancelled} predição(ões) cancelada(s).`,
      })
      refetchStatus()
    } catch {
      toast({ status: 'error', title: 'Erro ao cancelar predições.' })
    } finally {
      setIsCancelling(false)
    }
  }

  return (
    <Alert
      status="warning"
      w="full"
      maxW="unset"
      gap={4}
      mt="0 !important"
      py="3xs"
    >
      <Icon name="exclamation-triangle" size="xs" variant="highlight" />
      <Flex justify="space-between" align="center" w="full">
        <Stack color="black">
          <Text fontWeight="bold">
            A predição do ano de 2026 está em andamento!
          </Text>
          <Text mt="0 !important">
            Em até 48h os dados da predição estarão disponíveis para você.
          </Text>
        </Stack>
        {isDev && (
          <Button
            label="Cancelar predições"
            variant="secondary"
            border="square"
            onClick={handleCancel}
            isLoading={isCancelling}
            disabled={isCancelling}
          />
        )}
      </Flex>
    </Alert>
  )
}

const EmptySearch = () => {
  return (
    <Stack
      px={8}
      py={12}
      gap={4}
      align="center"
      boxShadow="md"
      borderRadius="base"
    >
      <Icon name="search" size="md" shaped variant="neutral" />
      <Stack>
        <Typography
          variant="s-md"
          color="brand.neutral.dark_2"
          textAlign="center"
        >
          Nenhum resultado encontrado.
        </Typography>
        <Typography
          variant="b-lg"
          color="brand.neutral.dark_2"
          textAlign="center"
        >
          Verifique se os termos foram digitados corretamente e se filtros que
          possam interferir na busca estão selecionados.
        </Typography>
      </Stack>
    </Stack>
  )
}

export default Predictions
